import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { ExclamationTriangleIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '../../../context/DarkModeContext';
import { InternshipResult } from '../../../types/internship';

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (deleteFiles: boolean) => void;
  result: InternshipResult;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  result
}) => {
  const { darkMode } = useDarkMode();
  const [deleteFiles, setDeleteFiles] = useState(false);
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const checkboxBg = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  
  const handleConfirm = () => {
    onConfirm(deleteFiles);
    onClose();
  };
  
  const documentCount = result.documents?.length || 0;
  
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative z-50"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      {/* Full-screen container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className={`mx-auto max-w-md rounded-lg ${bgColor} p-6 shadow-xl`}>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
            </div>
            <div className="flex-1">
              <Dialog.Title className={`text-lg font-semibold ${textColor}`}>
                Delete Internship Result?
              </Dialog.Title>
              
              <div className="mt-2 space-y-3">
                <p className={mutedTextColor}>
                  This will permanently remove your recorded outcome and all related information:
                </p>
                
                <ul className={`list-disc list-inside space-y-1 ${mutedTextColor} text-sm`}>
                  <li>Outcome type ({result.outcomeType?.replace('_', ' ')})</li>
                  <li>All details and reflections</li>
                  <li>Decision information</li>
                  <li>Goal achievements and lessons learned</li>
                </ul>
                
                {documentCount > 0 && (
                  <div className={`mt-4 p-3 rounded-lg border ${borderColor} ${checkboxBg}`}>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={deleteFiles}
                        onChange={(e) => setDeleteFiles(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <p className={`font-medium ${textColor}`}>
                          Also delete attached documents
                        </p>
                        <p className={`text-sm mt-1 ${mutedTextColor}`}>
                          {documentCount} document{documentCount !== 1 ? 's' : ''} will be permanently deleted.
                          If unchecked, documents will remain accessible in your Files.
                        </p>
                      </div>
                    </label>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-white'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                >
                  Delete Result
                </button>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default DeleteConfirmModal;